import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { getSessionToken as getStoredSessionToken, getVolunteerByAccessToken, initAuthAPI } from '../services/api';
import { Volunteer } from '../models/types';

interface AuthContextType {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isJobManager: boolean;
  isQRCodeManager: boolean;
  sessionToken: string | null;
  volunteer: Volunteer | null;
  loading: boolean;
  setAuthState: (params: { 
    isAuth: boolean; 
    isAdmin: boolean; 
    token: string | null;
    volunteer?: Volunteer | null; 
    callback?: () => void;
  }) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isJobManager, setIsJobManager] = useState<boolean>(false);
  const [isQRCodeManager, setIsQRCodeManager] = useState<boolean>(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [volunteer, setVolunteer] = useState<Volunteer | null>(null);
  const [loading, setLoading] = useState<boolean>(true);


  useEffect(() => {
    initAuthAPI({
      isAuthenticated,
      isAdmin,
      sessionToken,
      volunteer
    });
  }, [isAuthenticated, isAdmin, sessionToken, volunteer]);

  // Helper function to determine roles from volunteer data
  const updateRolesFromVolunteer = (volunteer: Volunteer | null) => {
    if (!volunteer) {
      setIsAdmin(false);
      setIsJobManager(false);
      setIsQRCodeManager(false);
      return;
    }
    
    const role = volunteer.role || 'Volunteer';
    setIsAdmin(role === 'Admin');
    setIsJobManager(role === 'JobManager' || role === 'Admin'); // Admin has all privileges
    setIsQRCodeManager(role === 'QRCodeManager' || role === 'Admin'); // Admin has all privileges
  };  useEffect(() => {
    const loadInitialState = async () => {
      setLoading(true);
      const token = getStoredSessionToken();
      
      if (!token) {
        setLoading(false);
        return;
      }
        setSessionToken(token);
      setIsAuthenticated(true);
      
      setIsAdmin((sessionStorage.getItem('isAdmin') === 'true'));
      
      try {
        const refreshedVolunteer = await getVolunteerByAccessToken(token);
        setVolunteer(refreshedVolunteer);
        updateRolesFromVolunteer(refreshedVolunteer);
        sessionStorage.setItem('volunteer', JSON.stringify(refreshedVolunteer));
      }
       catch (err) {          console.error('Error during auto-login or token validation:', err);
        const error = err as { response?: { status: number } };
        if (error.response?.status === 401) {
          handleLogout();
        }
        setLoading(false);
        return;
      }
      
      setLoading(false);
    };

    loadInitialState();
  }, []);

  const setAuthState = ({ isAuth, isAdmin, token, volunteer, callback }: { 
    isAuth: boolean; 
    isAdmin: boolean; 
    token: string | null;
    volunteer?: Volunteer | null;
    callback?: () => void;
  }) => {

    try {
      flushSync(() => {
        setIsAuthenticated(isAuth);
        setSessionToken(token);
        
        if (volunteer !== undefined) {
          setVolunteer(volunteer);
          updateRolesFromVolunteer(volunteer);
          if (volunteer) {
            sessionStorage.setItem('volunteer', JSON.stringify(volunteer));
          } else {
            sessionStorage.removeItem('volunteer');
          }
        } else {
          // Only set isAdmin if no volunteer data is provided (for backwards compatibility)
          setIsAdmin(isAdmin);
          setIsJobManager(false);
          setIsQRCodeManager(false);
        }
      });
  
      if (callback) {
        console.log('Executing callback with updated auth state');
        callback();
      }
    } catch (error) {
      console.error('Error updating auth state:', error);
    }
  };

  const handleLogout = async () => {
    try {

      const { logout } = await import('../services/api');
      await logout();
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {

      setIsAuthenticated(false);
      setIsAdmin(false);
      setIsJobManager(false);
      setIsQRCodeManager(false);
      setSessionToken(null);
      setVolunteer(null);

      sessionStorage.removeItem('sessionToken');
      sessionStorage.removeItem('isAdmin');
      sessionStorage.removeItem('volunteer');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isAdmin,
        isJobManager,
        isQRCodeManager,
        sessionToken,
        volunteer,
        loading,
        setAuthState,
        logout: handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

